import React from "react";
import clsx from "clsx";
import { Text } from "@jasperlabs/ui";
import { useCaptureGraphQLError } from "@/api";

import { useEffectOnce } from "@jasperlabs/hooks";

export type SomethingWentWrongTrigger = Error | { message: string };

export const useCaptureSomethingWentWrong = ({
	trigger,
}: {
	trigger?: SomethingWentWrongTrigger;
}) => {
	const captureWhatWentWrong = useCaptureGraphQLError();
	useEffectOnce(() => {
		if (trigger) {
			captureWhatWentWrong(trigger);
		}
	});
};

type SomethingWentWrongMessageProps = {
	isSmall?: boolean;
};

const SomethingWentWrongMessage = ({
	isSmall = false,
}: SomethingWentWrongMessageProps) => (
	<div className={clsx("grid", isSmall ? "gap-4" : "gap-6 md:gap-9")}>
		<Text
			as="h1"
			variant={isSmall ? "heading-xs" : "heading-sm"}
			className="text-center"
		>
			Something went wrong.
		</Text>
		<Text
			as="p"
			variant={isSmall ? "body-lg" : "body-xl"}
			className="text-center"
		>
			Refresh the page or try again later.
		</Text>
	</div>
);

export type SomethingWentWrongProps = React.ComponentProps<"div"> & {
	isSmall?: boolean;
	trigger?: SomethingWentWrongTrigger;
};

const SomethingWentWrong = ({
	trigger = new Error(
		"Something went wrong > An error occurred, but no explicit trigger was specified",
	),
	isSmall = false,
	...rest
}: SomethingWentWrongProps) => {
	useCaptureSomethingWentWrong({ trigger });

	return (
		<div className="max-w-container-lg mx-auto w-full" {...rest}>
			<div className="grid grid-cols-12">
				<div className="col-span-12 md:col-span-8 md:col-start-3">
					<SomethingWentWrongMessage isSmall={isSmall} />
				</div>
			</div>
		</div>
	);
};

SomethingWentWrong.Message = SomethingWentWrongMessage;

export default SomethingWentWrong;
